import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Device, DeviceAdapter } from 'src/app/models/device.model';
import { Dropdown } from 'src/app/models/dropdown.model';
import { DropdownsService } from 'src/app/services/dropdowns/dropdowns.service';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

interface ExtendedDropdown extends Dropdown {
  displayDescription?: string;
}

@Component({
  selector: 'app-upload-dialog',
  templateUrl: './upload-dialog.component.html',
  styleUrls: ['./upload-dialog.component.scss']
})
export class UploadDialogComponent implements OnInit {
  public faCalendarAlt = faCalendarAlt;
  devices: any[] = [];
  deviceGroups: ExtendedDropdown[] = [];
  deviceTypes: ExtendedDropdown[] = [];
  sensorProfiles: ExtendedDropdown[] = [];
  statuses: ExtendedDropdown[] = [];

  constructor(
    public dialogRef: MatDialogRef<UploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { devices: any[] },
    private _fb: FormBuilder,
    private _dropdownService: DropdownsService,
    private _deviceAdapter: DeviceAdapter
  ) {
    // Remove duplicates based on deviceId
    const uniqueDevices = Array.from(
      new Map(data.devices.map(item => [item.deviceId, item])).values()
    );
    this.devices = uniqueDevices;
  }

  ngOnInit() {
    this._getDeviceGroups();
    this._getDeviceTypes();
    this._getSensorProfiles();
    this._getStatuses();
  }


  private _getDeviceGroups() {
    this._dropdownService.getDeviceGroups().subscribe(res => {
      this.deviceGroups = res;
    });
  }

  private _getDeviceTypes() {
    this._dropdownService.getDeviceTypes().subscribe(res => {
      let filteredTypes = res.filter(item => item.code == 3 || item.code == 5).map(item => {
        let displayDescription = item.code == 3 ? 'Gateway' : 'ThingSendNode';
        return {
          ...item,
          displayDescription: displayDescription
        }
      });
    
      this.deviceTypes = filteredTypes;
    });
  }

  private _getSensorProfiles() {
    this._dropdownService.getSensorProfiles().subscribe(res => {
      let filteredProfiles = res.filter(item => item.code == 2 || item.code == 4 || item.code == 5).map(item => {
        let displayDescription = item.code == 2 ? 'SensorTemp (Temp)' : item.code == 4 ? 'SensorTHM (Temp, Humidity)' : 
        'SensorTHCO (Temp, Humidity, CO2)';
        return {
          ...item,
          displayDescription: displayDescription
        }
      });
      this.sensorProfiles = filteredProfiles;
    });
  }

  private _getStatuses() {
    this._dropdownService.getStatuses().subscribe(res => {
      this.statuses = res;
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    
    const enrichedDevices = this.devices.map(device => {
      const deviceData = new Device(
        device.deviceId,
        1,
        device.deviceType,
        device.sensorProfile,
        '',
        device.hwRevision,
        device.fwRevision,
        device.startOfService,
        device.endOfLifecycle,
        device.endOfLicense,
        device.statusId
      );
      return deviceData;
    });

    this.dialogRef.close(enrichedDevices);
  }

  getDeviceTypeName(deviceType: any): string {
    if (!deviceType) return '-';
    
    if (typeof deviceType === 'number') {
      const type = this.deviceTypes.find(dt => dt.code === deviceType);
      return type ? (type.displayDescription || type.description) : `Code: ${deviceType}`;
    }
    
    const typeByDesc = this.deviceTypes.find(dt => 
      dt.description.toLowerCase() === deviceType.toString().toLowerCase() ||
      (dt.displayDescription && dt.displayDescription.toLowerCase() === deviceType.toString().toLowerCase())
    );
    return typeByDesc ? (typeByDesc.displayDescription || typeByDesc.description) : deviceType.toString();
  }

  getSensorProfileName(sensorProfile: any): string {
    if (!sensorProfile) return '-';
    
    if (typeof sensorProfile === 'number') {
      const profile = this.sensorProfiles.find(sp => sp.code === sensorProfile);
      return profile ? (profile.displayDescription || profile.description) : `Code: ${sensorProfile}`;
    }
    
    const profileByDesc = this.sensorProfiles.find(sp => 
      sp.description.toLowerCase() === sensorProfile.toString().toLowerCase() ||
      (sp.displayDescription && sp.displayDescription.toLowerCase() === sensorProfile.toString().toLowerCase())
    );
    return profileByDesc ? (profileByDesc.displayDescription || profileByDesc.description) : sensorProfile.toString();
  }

  getStatusName(statusId: any): string {
    if (!statusId) return '-';
    
    if (typeof statusId === 'number') {
      const status = this.statuses.find(s => s.code === statusId);
      return status ? status.description : `Code: ${statusId}`;
    }
    
    const statusByDesc = this.statuses.find(s => 
      s.description.toLowerCase() === statusId.toString().toLowerCase()
    );
    return statusByDesc ? statusByDesc.description : statusId.toString();
  }

  formatDate(date: any): string {
    if (!date) return '-';
    
    try {
      let dateObj: Date;
      
      if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'number') {
        // Handle Excel serial number dates
        const excelEpoch = new Date(1899, 11, 30);
        dateObj = new Date(excelEpoch.getTime() + (date * 24 * 60 * 60 * 1000));
      } else {
        dateObj = new Date(date);
      }
      
      if (isNaN(dateObj.getTime())) return 'Invalid Date';
      
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      console.warn('Error formatting date:', date, error);
      return 'Invalid Date';
    }
  }
} 